import React, { Suspense, lazy } from "react";
import { Spin, theme } from "antd";

import { Model3DViewerProps } from "./types";

// Lazy so three.js and the R3F renderer stay out of the main chunk — most sessions never open a viewer.
const Model3DScene = lazy(() => import("./Model3DScene"));

const Model3DViewer: React.FC<Model3DViewerProps> = ({
	points,
	lines,
	meshes,
	bounds,
	height = 320,
	background,
	foreground,
	autoRotate = false,
	ariaLabel = "Interactive 3D model",
	loadingFallback,
	className,
	style,
}) => {
	const { token } = theme.useToken();

	const surface = background ?? token.colorBgLayout;
	const stroke = foreground ?? token.colorTextSecondary;

	const fallback = loadingFallback ?? (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
			<Spin size="small" />
		</div>
	);

	return (
		<div
			role="img"
			aria-label={ariaLabel}
			data-testid="model-3d-viewer"
			className={className}
			style={{
				height,
				width: "100%",
				background: surface,
				borderRadius: token.borderRadius,
				border: `1px solid ${token.colorBorderSecondary}`,
				overflow: "hidden",
				...style,
			}}
		>
			<Suspense fallback={fallback}>
				<Model3DScene
					points={points}
					lines={lines}
					meshes={meshes}
					bounds={bounds}
					background={surface}
					foreground={stroke}
					autoRotate={autoRotate}
				/>
			</Suspense>
		</div>
	);
};

export default Model3DViewer;
